import { coerce, gte } from 'semver';

/**
 * Checks if a given version string is greater than or equal to a minimum version.
 *
 * Useful for validating semantic version requirements (e.g. "2.3.0" >= "2.0.0").
 * If either version cannot be coerced to a valid semver, returns false.
 *
 * @param v - The version string to test
 * @param min - The minimum version string to compare against
 * @returns True if `v` is greater than or equal to `min`, false otherwise
 */
export function isVersionAtLeast(v: string, min: string): boolean {
  const coercedV = coerce(v);
  const coercedMin = coerce(min);

  if (!coercedV || !coercedMin) {
    return false;
  }

  return gte(coercedV, coercedMin);
}

/**
 * Returns a selected value based on whether an update is happening.
 *
 * If `update` is true, returns `partValue` (or null if it's undefined/null).
 * If `update` is false, returns `partValue` if defined, otherwise returns
 * the first fallback ID from the array (or null).
 *
 * @param update - Whether to prioritise the existing `partValue`
 * @param partValue - The value to prefer if it's defined
 * @param fallbackArray - Optional fallback array of objects with `id` keys
 * @returns A value of type T, or null if nothing suitable is found
 */
export function getSelectedValue<T>(
  update: boolean,
  partValue: T | undefined | null,
  fallbackArray: { id: T }[] | undefined
): T | null {
  return update
    ? partValue ?? null
    : partValue ?? fallbackArray?.[0]?.id ?? null;
}

/**
 * Removes all properties from an object whose values are empty strings ("").
 * E.G. { key_one: "value_one", key_two: "" }; becomes { key_one: "value_one" }
 *
 * @param obj
 */
export function removeEmptyStrings<T extends Record<string, any>>(obj: T): { [p: string]: any } {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== "")
  );
}

/**
 * Recursively wraps an object and all its nested properties in a Proxy
 * to detect and react to mutations.
 *
 * Useful for observing deep changes in objects (e.g. Plotly layouts) where
 * Angular change detection or state watchers won’t pick up direct mutations.
 *
 * @param obj - The object to wrap
 * @param path - Used internally for tracking nested property paths in logs
 * @param triggerChange - Optional callback to run on any mutation
 * @returns A deeply proxied version of the object
 */
export function wrapDeep<T extends object>(
  obj: T,
  path = '',
  triggerChange?: () => void
): T {
  return new Proxy(obj, {
    get(target, prop) {
      const value = target[prop as keyof T];
      if (typeof value === 'object' && value !== null) {
        return wrapDeep(value as any, `${path}.${String(prop)}`, triggerChange);
      }
      return value;
    },
    set(target, prop, value) {
      const result = Reflect.set(target, prop, value);
      triggerChange?.();
      return result;
    },
  });
}

/**
 * Formats a duration in seconds into a conversational ETA string.
 *
 * Examples:
 *   30   → "< 1 min"
 *   90   → "1 min"
 *   3700 → "1 hr 1 min"
 *   7200 → "2 hrs"
 *   90000 → "1 day 1 hr"
 *
 * @param seconds - Duration in seconds
 * @returns A human-friendly string
 */
export function formatJobDuration(seconds: number): string {
  if (seconds < 60) {
    return '< 1 min';
  }

  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const remainingMinutes = minutes % 60;
  const remainingHours = hours % 24;

  if (days > 0) {
    if (remainingHours === 0 && remainingMinutes === 0) {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (remainingMinutes === 0) {
      return `${days} day${days > 1 ? 's' : ''} ${remainingHours} hr${remainingHours > 1 ? 's' : ''}`;
    }
    return `${days} day${days > 1 ? 's' : ''} ${remainingHours} hr${remainingHours > 1 ? 's' : ''} ${remainingMinutes} min`;
  }

  if (hours > 0) {
    if (remainingMinutes === 0) {
      return `${hours} hr${hours > 1 ? 's' : ''}`;
    }
    return `${hours} hr ${remainingMinutes} min`;
  }

  return `${minutes} min`;
}
